import { Component, OnInit, OnDestroy, inject, Injectable, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, NavigationEnd } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { Observable, firstValueFrom, of, catchError, finalize, lastValueFrom, Subscription, filter } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';
import { MatIconModule } from '@angular/material/icon';
import { EnvironmentService } from '../../shared/services/environment.service';
import { TransactionService } from '../../shared/services/transaction.service';
import { NotificationService } from '../../shared/services/notification.service';
import { SettingsService, MainPageSettings } from '../../shared/services/settings.service';
import { MockTableData } from '../../mocks/mock-table-data';
import { MockMessageData } from '../../mocks/mock-message-data';
import { MockChartData } from '../../mocks/mock-chart-data';
import { MockTransactionData } from '../../mocks/mock-transaction-data';
import { ApproveStatus, Transaction } from '../../models/transaction.model';
import { NotificationItem, getNotificationTypeInfo, NotificationSummary, getNotificationSummaryConfig } from '../../models/notification.model';
@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  getThemeColor(colorName: string): string {
    return getComputedStyle(document.documentElement)
      .getPropertyValue(`--color-${colorName}`)
      .trim();
  }
}

@Component({
  selector: 'app-mainpage',
  standalone: true,
  imports: [CommonModule, TranslateModule, MatIconModule],
  templateUrl: './mainPage.component.html',
  styleUrls: ['./mainPage.component.less']
})

export class MainPageComponent implements OnInit, OnDestroy {
  private _http = inject(HttpClient);
  private _router = inject(Router);
  private _environment = inject(EnvironmentService);
  private _txService = inject(TransactionService);
  private _notificationService = inject(NotificationService);
  private _settingsService = inject(SettingsService);

  recentNotifications: NotificationItem[] = [];
  notificationSummary: NotificationSummary[] = [];
  public getNotificationTypeInfo = getNotificationTypeInfo;
  loading = signal(true);
  error = '';
  currentTime = new Date();
  settings: MainPageSettings = this._settingsService.getSettings();

  private _routerSub?: Subscription;
  private _pushSub?: Subscription;
  private _timeInterval?: ReturnType<typeof setInterval>;

  ngOnInit(): void {
    this.notificationSummary = getNotificationSummaryConfig();
    this.loadMainPageData();
    this.setupNotificationSummary();
    this.settings = this._settingsService.getSettings();

    this._timeInterval = setInterval(() => {
      this.currentTime = new Date();
    }, 60000);

    this._routerSub = this._router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      filter(event => event.urlAfterRedirects === '/' || event.urlAfterRedirects === '')
    ).subscribe(() => {
      console.log('[MainPage] Route re-activated, refreshing data...');
      this.loadMainPageData();
      this.settings = this._settingsService.getSettings();
    });

    this._pushSub = this._notificationService.pushReceived$.subscribe(event => {
      console.log('[MainPage] Push notification received (type:', event.type, '), refreshing data...');
      this.loadMainPageData();
    });
  }

  ngOnDestroy(): void {
    this._routerSub?.unsubscribe();
    this._pushSub?.unsubscribe();
    if (this._timeInterval) {
      clearInterval(this._timeInterval);
    }
  }

  private getThemeColor(colorName: string): string {
    return getComputedStyle(document.documentElement)
      .getPropertyValue(`--color-${colorName}`)
      .trim();
  }

  setupNotificationSummary(): void {
    this.notificationSummary = [
      {
        type: 0,
        typeNameKey: 'MAINPAGE.TRANSACTIONS',
        count: 0,
        icon: 'credit_card',
        color: this.getThemeColor('transactions'),
        route: '/transaction'
      },
      {
        type: 1,
        typeNameKey: 'MAINPAGE.MESSAGES',
        count: 0,
        icon: 'mail',
        color: this.getThemeColor('messages'),
        route: '/message'
      },
      {
        type: 2,
        typeNameKey: 'MAINPAGE.TABLES',
        count: 0,
        icon: 'table_chart',
        color: this.getThemeColor('tables'),
        route: '/table'
      },
      {
        type: 3,
        typeNameKey: 'MAINPAGE.CHARTS',
        count: 0,
        icon: 'insert_chart',
        color: this.getThemeColor('charts'),
        route: '/chart'
      }
    ];
  }

  getSummaryCardClass(summary: NotificationSummary): string {
    const typeMap = {
      0: 'transactions',
      1: 'messages',
      2: 'tables',
      3: 'charts'
    };
    return typeMap[summary.type as keyof typeof typeMap] || '';
  }

  async loadMainPageData(): Promise<void> {
    console.log('[MainPage] Loading recent notifications...');
    this.loading.set(true);
    this.error = '';

    const mockList: NotificationItem[] = [
      ...MockTableData.getTableList(),
      ...MockMessageData.getMessageList(),
      ...MockChartData.getChartList(),
      ...MockTransactionData.getTransactionList().map<NotificationItem>(tx => ({
        notificationID: tx.id,
        title: tx.title,
        body: tx.message,
        type: 0,
        created: tx.createdAt,
        senderUserID: 100,
        apnsPriority: 1,
        status: tx.status,
        isRead: false,
        readAt: null
      }))
    ];

    const source$: Observable<NotificationItem[]> = this._environment.isDesignMode
      ? of(mockList)
      : this._http.get<NotificationItem[]>('/api/notifications');

    const allNotifications: NotificationItem[] = await firstValueFrom(
      source$.pipe(
        catchError(err => {
          console.error('[MainPage] Error loading API data:', err);
          this.error = 'Error loading main page data. Please try again.';
          return of([] as NotificationItem[]);
        }),
        finalize(() => {
          this.loading.set(false);
        })
      )
    );

    this.recentNotifications = allNotifications
      .sort((a: NotificationItem, b: NotificationItem) => new Date(b.created).getTime() - new Date(a.created).getTime())
      .slice(0, 6);

    await this.updateNotificationCounts(allNotifications);
  }

  async updateNotificationCounts(notifications: NotificationItem[]): Promise<void> {
    if (this._environment.isDesignMode) {
      for (const summary of this.notificationSummary) {
        if (summary.type === 0) {
          const mockTransactions = MockTransactionData.getTransactionList();
          summary.count = notifications.filter(n => n.type === summary.type && !n.isRead).length;
          summary.importedCount = mockTransactions.filter((tx: Transaction) => tx.status === ApproveStatus.Imported).length;
        } else {
          summary.count = notifications.filter(n => n.type === summary.type && !n.isRead).length;
        }
      }
    } else {
      console.log('[MainPage] Fetching unread counts from API...');

      const unreadCountsByType = await lastValueFrom(
        this._notificationService.getUnreadCountsByType().pipe(
          catchError(err => {
            console.error('[MainPage] Error fetching unread counts:', err);
            return of({} as Record<number, number>);
          })
        )
      );

      console.log('[MainPage] Received unread counts:', unreadCountsByType);

      for (const summary of this.notificationSummary) {
        if (summary.type === 0) {
          const transactions = await lastValueFrom(
            this._http.get<any[]>('/api/transaction').pipe(
              catchError(err => {
                console.error('[MainPage] Error loading transactions for count:', err);
                return of([] as any[]);
              })
            )
          );

          summary.count = unreadCountsByType[summary.type] || 0;
          summary.importedCount = transactions.filter((tx: any) => tx.status === ApproveStatus.Imported).length;

          console.log('[MainPage] Transaction counts - Unread:', summary.count, 'Imported:', summary.importedCount);
        } else {
          summary.count = unreadCountsByType[summary.type] || 0;
          console.log(`[MainPage] Type ${summary.type} unread count:`, summary.count);
        }
      }
    }
  }

  onNotificationClick(notification: NotificationItem): void {
    const routes = {
      0: '/transaction/detail',
      1: '/message/detail',
      2: '/table/detail',
      3: '/chart/detail'
    };

    const route = routes[notification.type as keyof typeof routes];
    if (route) {
      this._router.navigate([route, notification.notificationID]);
    }
  }

  onSummaryCardClick(summary: NotificationSummary): void {
    this._router.navigate([summary.route]);
  }

  formatDate(dateString: string): string {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));

    if (diffInMinutes < 1) return 'Nyní';
    if (diffInMinutes < 60) return `Před ${diffInMinutes} min`;
    if (diffInMinutes < 1440) return `Před ${Math.floor(diffInMinutes / 60)} h`;

    return date.toLocaleString('cs-CZ', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  getDisplayCount(summary: NotificationSummary): string {
    if (summary.type === 0) {
      return `${summary.count || 0} / ${summary.importedCount || 0}`;
    } else {
      return (summary.count || 0).toString();
    }
  }

  getPreviewText(body: string, maxLength: number = 80): string {
    if (!body) return '';
    return body.length > maxLength ? body.substring(0, maxLength) + '...' : body;
  }

  getGreeting(): string {
    const hour = this.currentTime.getHours();
    if (hour < 12) return 'MAINPAGE.GOOD_MORNING';
    if (hour < 18) return 'MAINPAGE.GOOD_AFTERNOON';
    return 'MAINPAGE.GOOD_EVENING';
  }

  navigateToNotifications(): void {
    this._router.navigate(['/notifications']);
  }

  navigateToTransactions(): void {
    this._router.navigate(['/transaction']);
  }

  navigateToMessages(): void {
    this._router.navigate(['/message']);
  }

  navigateToTables(): void {
    this._router.navigate(['/table']);
  }

  navigateToCharts(): void {
    this._router.navigate(['/chart']);
  }
}
